import { useCallback, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import { Button, EmptyState, ErrorState, Modal, Panel, Tabs } from '../components/ui';
import { formatDate, formatNumber } from '../lib/format';
import { WorldBossAdmin } from '../components/admin/WorldBossAdmin';
import { ErrorBoundary } from '../components/ErrorBoundary';

/* =========================================================================
   Admin -> Events.
   =========================================================================

   The whole point of the Events framework is that a new event is a form, not a
   release. This is that form.

   Two rules shape the layout. **Nothing destructive happens without the impact
   being stated first** — publishing, ending, granting in bulk and configuring a
   Pokemon all show what they will do to how many people before the button does
   anything (PART 51). And **the server owns every decision**: this page renders
   the catalog it is handed, posts intent, and never computes a reward, a
   threshold or an eligibility list of its own.
   ========================================================================= */

type Status = 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'PAUSED' | 'ENDED' | 'ARCHIVED';

interface EventRow {
  key: string;
  name: string;
  summary: string;
  theme: string;
  status: Status;
  eventType: string;
  visible: boolean;
  featured: boolean;
  priority: number;
  startsAt: string;
  endsAt: string;
  timezone: string;
  participants: number;
  objectives: number;
  tiers: number;
  rewards: number;
  claims: number;
}

interface CatalogOption {
  key: string;
  label: string;
  cap?: number;
  /** Modifiers only. False = the game does not read this key yet. */
  implemented?: boolean;
}

interface Catalog {
  actions: CatalogOption[];
  rewardKinds: CatalogOption[];
  modifiers: CatalogOption[];
  eligibility: CatalogOption[];
  statuses: Status[];
  eventTypes: string[];
  items: Array<{ key: string; name: string; category: string }>;
  eggFamilies: Array<{ key: string; name: string }>;
  badges: Array<{ key: string; name: string }>;
  titles: Array<{ key: string; name: string }>;
  ribbons: Array<{ key: string; name: string }>;
  species: Array<{ id: number; displayName: string }>;
}

interface ValidationIssue {
  level: 'error' | 'warning';
  where: string;
  message: string;
}

/**
 * The shape `GET /admin/events/:key` returns.
 *
 * Written out rather than inferred because this is the payload the editor
 * hydrates from, and a field the server renames should break the build here
 * rather than silently arrive as `undefined` in a form somebody then saves.
 */
interface LoadedEvent {
  key: string;
  name: string;
  summary: string | null;
  description: string | null;
  theme: string | null;
  eventType: string | null;
  status: Status;
  startsAt: string;
  endsAt: string;
  timezone: string | null;
  visible: boolean;
  featured: boolean;
  priority: number;
  pointsName: string | null;
  currencyName: string | null;
  currencyEndBehaviour: string | null;
  currencyConvertRate: number;
  modifiers: Record<string, unknown>;
  objectives: Array<{
    key: string;
    name: string;
    kind: string;
    filter: string;
    target: number;
    pointsEach: number;
    currencyEach: number;
    pointsOnComplete: number;
    repeatable: boolean;
    dailyLimit: number | null;
    accountLimit: number | null;
    progressDisplay: string;
    enabled: boolean;
  }>;
  rewardDefs: Array<{
    /** Needed to resolve `tiers[].rewards[].rewardDefId` back to a reward key. */
    id: string;
    key: string;
    label: string;
    kind: string;
    config: string;
    quantity: number;
    enabled: boolean;
  }>;
  tiers: Array<{
    threshold: number;
    label: string;
    autoGrant: boolean;
    eligibility: string;
    eligibilityConfig: string;
    enabled: boolean;
    rewards: Array<{ rewardDefId: string; sortOrder: number }>;
  }>;
}

interface PokemonPreview {
  species: { id: number; displayName: string; spriteFront: string | null; spriteShiny: string | null };
  level: number;
  gender: string;
  isShiny: boolean;
  nature: { id: number; name: string } | null;
  originLabel: string;
  originalTrainer: string;
  randomised: { ivs: string[]; gender: boolean; nature: boolean };
}

interface Analytics {
  participation: {
    participants: number;
    activeLast24h: number;
    pointsTotal: number;
    pointsAverage: number;
    pointsBest: number;
    objectivesCompleted: number;
  };
  rewards: { claimsTotal: number };
  pokemon: {
    generated: number;
    shiny: number;
    withOriginalTrainer: number;
    traded: number;
    released: number;
  };
}

/** The order the board reads in: what needs doing, then what is on, then history. */
const STATUS_ORDER: Status[] = ['DRAFT', 'SCHEDULED', 'ACTIVE', 'PAUSED', 'ENDED', 'ARCHIVED'];

/**
 * The themes the stylesheet has an accent rule for.
 *
 * A free-text field let an admin type a key with no matching CSS and get the
 * default silently — the same failure a seeded cosmetic with no stylesheet rule
 * would be. A picker cannot miss.
 */
const THEMES = ['festival', 'harvest', 'winter', 'spooky', 'anniversary', 'blossom'];

/**
 * One class per status, setting `--status`.
 *
 * The stripe, the chip and the section heading all read that variable, so a
 * status has exactly one colour wherever it appears, and adding a state is one
 * line here plus one in the stylesheet.
 */
const STATUS_CLASS: Record<Status, string> = {
  DRAFT: 'is-draft',
  SCHEDULED: 'is-scheduled',
  ACTIVE: 'is-active',
  PAUSED: 'is-paused',
  ENDED: 'is-ended',
  ARCHIVED: 'is-archived',
};

export function AdminEventsPage() {
  const { me } = useSession();
  const toast = useToast();
  const [selected, setSelected] = useState<string | null>(null);
  // A World Boss *is* an event, so it lives behind a tab on this page rather
  // than at its own admin route: dates, copy, theme and reward definitions are
  // authored on the Events tab and the boss tab configures the fight that hangs
  // off one. Two pages would be two places to look for the same event.
  const [section, setSection] = useState<'events' | 'worldboss'>('events');

  const state = useAsync<{ events: EventRow[] }>(() => api.get('/admin/events'));
  const catalog = useAsync<Catalog>(() => api.get('/admin/events/catalog'));

  const can = useCallback(
    (permission: string) => Boolean(me?.permissions?.includes(permission)),
    [me],
  );

  const grouped = useMemo(() => {
    const map = new Map<Status, EventRow[]>();
    for (const status of STATUS_ORDER) map.set(status, []);
    for (const event of state.data?.events ?? []) {
      map.get(event.status)?.push(event);
    }
    return map;
  }, [state.data]);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Events</h1>
          <div className="page-head__sub">
            Create, schedule and run limited-time events. Nothing here needs a deploy.
          </div>
        </div>
        {can('events.manage') && section === 'events' && (
          <Button onClick={() => setSelected('__new__')}>New event</Button>
        )}
      </div>

      <Tabs
        tabs={[
          { value: 'events', label: 'Events' },
          { value: 'worldboss', label: 'World Boss' },
        ]}
        value={section}
        onChange={(next) => setSection(next as 'events' | 'worldboss')}
      />

      {section === 'worldboss' ? (
        /*
         * Its own boundary, so a fault in the newest and least-exercised part
         * of this console cannot take the whole of Admin -> Events down with
         * it. Running events are managed from this page; losing the ability to
         * pause one because a World Boss panel threw is a much worse failure
         * than losing the World Boss panel.
         */
        <ErrorBoundary>
          <WorldBossAdmin />
        </ErrorBoundary>
      ) : state.loading ? (
        <div className="pxwell skeleton" style={{ height: 260 }} />
      ) : (state.data?.events.length ?? 0) === 0 ? (
        <Panel>
          <EmptyState art="✦" title="No events yet">
            Run <code>npm run seed:events</code> for a starter template, or create one from scratch.
          </EmptyState>
        </Panel>
      ) : (
        /* One panel with headed sections, rather than a Panel per status. Six
           statuses meant six stacked boxes for what is usually a handful of
           events, and the chrome outweighed what it framed. */
        <Panel>
          <div className="admin-event-board">
            {STATUS_ORDER.map((status) => {
              const rows = grouped.get(status) ?? [];
              if (rows.length === 0) return null;
              return (
                <div key={status} className={STATUS_CLASS[status]}>
                  <div className="admin-event-group__head">
                    <span className="admin-event-group__title">{status}</span>
                    <span className="admin-event-group__count">
                      {rows.length} {rows.length === 1 ? 'event' : 'events'}
                    </span>
                  </div>
                  <div className="admin-event-list">
                    {rows.map((event) => (
                      <EventRowCard key={event.key} event={event} onOpen={() => setSelected(event.key)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {section === 'events' && selected && catalog.data && (
        <EventEditor
          eventKey={selected === '__new__' ? null : selected}
          catalog={catalog.data}
          can={can}
          onClose={() => setSelected(null)}
          onSaved={() => {
            state.reload();
            toast.push('success', 'Saved.');
          }}
        />
      )}
    </div>
  );
}

function EventRowCard({ event, onOpen }: { event: EventRow; onOpen: () => void }) {
  return (
    <button
      type="button"
      className={`pxwell admin-event-row ${STATUS_CLASS[event.status]}`}
      onClick={onOpen}
    >
      <div className="admin-event-row__main">
        <div className="admin-event-row__name">
          {event.name}
          {event.featured && <span className="chip chip--gold">Featured</span>}
          {!event.visible && <span className="chip">Hidden</span>}
        </div>
        <div className="admin-event-row__sub">{event.summary || 'No summary yet'}</div>
        <div className="admin-event-row__dates">
          {formatDate(event.startsAt)} → {formatDate(event.endsAt)} · {event.timezone}
        </div>
      </div>
      <div className="admin-event-row__stats">
        {/* The status word travels with its colour. The stripe alone would be
            colour carrying meaning on its own, which the rest of this codebase
            is careful never to do. */}
        <span className="chip chip--status">{event.status}</span>
        <span>{formatNumber(event.participants)} taking part</span>
        <span className="admin-event-row__counts">
          {event.objectives}○ · {event.tiers}▸ · {event.rewards}◆
        </span>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------
   The editor
   ------------------------------------------------------------------------- */

interface ObjectiveDraft {
  key: string;
  name: string;
  kind: string;
  filter: Record<string, unknown>;
  target: number;
  pointsEach: number;
  currencyEach: number;
  pointsOnComplete: number;
  repeatable: boolean;
  dailyLimit: number | null;
  accountLimit: number | null;
  progressDisplay: string;
  enabled: boolean;
}

interface RewardDraft {
  key: string;
  label: string;
  kind: string;
  config: Record<string, unknown>;
  quantity: number;
  enabled: boolean;
}

interface TierDraft {
  threshold: number;
  label: string;
  autoGrant: boolean;
  eligibility: string;
  eligibilityConfig: Record<string, unknown>;
  enabled: boolean;
  rewards: string[];
}

interface EventDraft {
  key: string;
  name: string;
  summary: string;
  description: string;
  theme: string;
  eventType: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  visible: boolean;
  featured: boolean;
  priority: number;
  pointsName: string | null;
  currencyName: string | null;
  currencyEndBehaviour: string;
  currencyConvertRate: number;
  modifiers: Record<string, unknown>;
  objectives: ObjectiveDraft[];
  rewards: RewardDraft[];
  tiers: TierDraft[];
}

const emptyDraft = (): EventDraft => {
  const start = new Date(Date.now() + 86_400_000);
  const end = new Date(start.getTime() + 48 * 3_600_000);
  return {
    key: '',
    name: '',
    summary: '',
    description: '',
    theme: 'festival',
    eventType: 'SEASONAL',
    startsAt: start.toISOString().slice(0, 16),
    endsAt: end.toISOString().slice(0, 16),
    // Defaulted from the admin's own browser rather than to UTC. PART 45 is
    // about an event opening hours early because somebody typed a wall clock
    // and the server read it as another zone; the fix starts with not making
    // them pick their own timezone from a list every time.
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    visible: true,
    featured: false,
    priority: 0,
    pointsName: 'Event Points',
    currencyName: null,
    currencyEndBehaviour: 'KEEP',
    currencyConvertRate: 0,
    modifiers: {},
    objectives: [],
    rewards: [],
    tiers: [],
  };
};

const toLocalInput = (iso: string) => {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

function EventEditor({
  eventKey,
  catalog,
  can,
  onClose,
  onSaved,
}: {
  eventKey: string | null;
  catalog: Catalog;
  can: (permission: string) => boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [tab, setTab] = useState<'basics' | 'objectives' | 'rewards' | 'tiers' | 'modifiers' | 'review'>(
    'basics',
  );
  const [draft, setDraft] = useState<EventDraft | null>(eventKey ? null : emptyDraft());
  const [status, setStatus] = useState<Status>('DRAFT');
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<{ errors: ValidationIssue[]; warnings: ValidationIssue[] } | null>(
    null,
  );

  const loaded = useAsync<{ event: LoadedEvent | null }>(
    () =>
      eventKey
        ? api.get(`/admin/events/${eventKey}`)
        : Promise.resolve({ event: null }),
    [eventKey],
  );

  // Hydrate the draft once the server answers. Guarded on `draft === null` so a
  // background refresh can never clobber an edit somebody is halfway through.
  if (eventKey && loaded.data?.event && draft === null) {
    const event = loaded.data.event;
    const rewardKeyById = new Map(event.rewardDefs.map((def) => [def.id, def.key]));
    setStatus(event.status);
    setDraft({
      key: event.key,
      name: event.name,
      summary: event.summary ?? '',
      description: event.description ?? '',
      theme: event.theme ?? 'festival',
      eventType: event.eventType ?? 'SEASONAL',
      startsAt: toLocalInput(event.startsAt),
      endsAt: toLocalInput(event.endsAt),
      timezone: event.timezone ?? 'UTC',
      visible: event.visible,
      featured: event.featured,
      priority: event.priority,
      pointsName: event.pointsName,
      currencyName: event.currencyName,
      currencyEndBehaviour: event.currencyEndBehaviour ?? 'KEEP',
      currencyConvertRate: event.currencyConvertRate,
      modifiers: event.modifiers ?? {},
      objectives: event.objectives.map((row) => ({
        ...row,
        filter: safeJson(row.filter),
      })),
      rewards: event.rewardDefs.map((row) => ({
        ...row,
        config: safeJson(row.config),
      })),
      // Tier links arrive as reward *ids* and are saved back as reward *keys*,
      // so they have to be resolved here. Loading them as an empty array and
      // saving that back silently deletes every reward the tier granted — the
      // save path replaces links wholesale, which is safe only if what was
      // loaded is complete. Caught by opening the seeded event and pressing
      // Save, which reported four tiers granting nothing.
      tiers: event.tiers.map((row) => ({
        threshold: row.threshold,
        label: row.label,
        autoGrant: row.autoGrant,
        eligibility: row.eligibility,
        eligibilityConfig: safeJson(row.eligibilityConfig),
        enabled: row.enabled,
        rewards: row.rewards
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((link) => rewardKeyById.get(link.rewardDefId))
          .filter((key): key is string => key != null),
      })),
    });
  }

  const update = (patch: Partial<EventDraft>) => setDraft((current) => (current ? { ...current, ...patch } : current));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.post('/admin/events', {
        ...draft,
        startsAt: new Date(draft.startsAt).toISOString(),
        endsAt: new Date(draft.endsAt).toISOString(),
      });
      onSaved();
      const result = await api.get<{ errors: ValidationIssue[]; warnings: ValidationIssue[] }>(
        `/admin/events/${draft.key}/validate`,
      );
      setIssues(result);
      setTab('review');
    } catch (error) {
      toast.push('error', error instanceof ApiError ? error.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const act = async (path: string, body?: Record<string, unknown>) => {
    if (!draft) return;
    try {
      const result = await api.post<{ status: Status }>(`/admin/events/${draft.key}/${path}`, body ?? {});
      setStatus(result.status);
      onSaved();
      toast.push('success', `Event is now ${result.status}.`);
    } catch (error) {
      toast.push('error', error instanceof ApiError ? error.message : 'That did not work.');
    }
  };

  if (!draft) {
    return (
      <Modal onClose={onClose} labelledBy="admin-event-title">
        <h2 id="admin-event-title" className="panel__title">
          Loading
        </h2>
        <div className="pxwell skeleton" style={{ height: 200 }} />
      </Modal>
    );
  }

  return (
    // `size="work"` rather than `wide`: this is a form somebody will spend
    // twenty minutes in, and the dialog itself must not scroll — the header and
    // the Save button stay put while only the tab body moves. A tall form whose
    // only scroll is the whole dialog hides Save below the fold and makes every
    // edit end with a hunt for it.
    <Modal onClose={onClose} size="work" className="admin-event-editor" labelledBy="admin-event-title">
      <div className={`admin-event-editor__head ${STATUS_CLASS[status]}`}>
        <h2 id="admin-event-title" className="admin-event-editor__title">
          {eventKey ? draft.name || draft.key : 'New event'}
          <span className="chip chip--status">{status}</span>
        </h2>
        <LifecycleControls status={status} can={can} onAct={act} />
      </div>

      <div className="admin-event-editor__tabs">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { value: 'basics', label: 'Basics' },
            { value: 'objectives', label: `Objectives · ${draft.objectives.length}` },
            { value: 'rewards', label: `Rewards · ${draft.rewards.length}` },
            { value: 'tiers', label: `Tiers · ${draft.tiers.length}` },
            { value: 'modifiers', label: 'Bonuses' },
            { value: 'review', label: 'Review' },
          ]}
        />
      </div>

      <div className="admin-event-editor__body">
        {tab === 'basics' && <BasicsTab draft={draft} catalog={catalog} onChange={update} locked={!!eventKey} />}
        {tab === 'objectives' && <ObjectivesTab draft={draft} catalog={catalog} onChange={update} />}
        {tab === 'rewards' && (
          <RewardsTab draft={draft} catalog={catalog} onChange={update} can={can} eventKey={draft.key} />
        )}
        {tab === 'tiers' && <TiersTab draft={draft} catalog={catalog} onChange={update} />}
        {tab === 'modifiers' && <ModifiersTab draft={draft} catalog={catalog} onChange={update} />}
        {tab === 'review' && <ReviewTab issues={issues} eventKey={draft.key} status={status} can={can} />}
      </div>

      <div className="admin-event-editor__actions">
        <span className="admin-event-editor__hint">
          {status === 'DRAFT'
            ? 'Saving keeps this a draft — publishing is a separate, deliberate step.'
            : 'Changes take effect immediately for players.'}
        </span>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}

function safeJson(value: unknown): Record<string, unknown> {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>;
  try {
    return JSON.parse(String(value ?? '{}')) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * The lifecycle buttons.
 *
 * Only the transitions that are legal from the current status are shown at all,
 * rather than shown-and-disabled: a row of greyed buttons invites a click and
 * then explains why not, and the server refuses these anyway. The confirmations
 * are on the two that are hard to walk back.
 */
function LifecycleControls({
  status,
  can,
  onAct,
}: {
  status: Status;
  can: (permission: string) => boolean;
  onAct: (path: string, body?: Record<string, unknown>) => void;
}) {
  if (!can('events.publish')) return null;

  return (
    <div className="admin-event-editor__lifecycle">
      {status === 'DRAFT' && (
        <Button
          onClick={() => {
            if (confirm('Publish this event? It will open automatically at its start time.')) {
              onAct('publish');
            }
          }}
        >
          Publish
        </Button>
      )}
      {status === 'SCHEDULED' && (
        <>
          <Button variant="ghost" onClick={() => onAct('start-early')}>
            Start now
          </Button>
          <Button variant="ghost" onClick={() => onAct('end')}>
            Cancel
          </Button>
        </>
      )}
      {status === 'ACTIVE' && (
        <>
          <Button variant="ghost" onClick={() => onAct('pause')}>
            Pause
          </Button>
          <Button variant="ghost" onClick={() => onAct('extend', { hours: 24 })}>
            +24h
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              if (confirm('End this event now? Players will stop earning immediately.')) onAct('end');
            }}
          >
            End early
          </Button>
        </>
      )}
      {status === 'PAUSED' && (
        <Button onClick={() => onAct('resume')}>Resume</Button>
      )}
      {status === 'ENDED' && (
        <>
          <Button variant="ghost" onClick={() => onAct('extend', { hours: 24 })}>
            Reopen +24h
          </Button>
          <Button variant="ghost" onClick={() => onAct('archive')}>
            Archive
          </Button>
        </>
      )}
    </div>
  );
}

function BasicsTab({
  draft,
  catalog,
  onChange,
  locked,
}: {
  draft: EventDraft;
  catalog: Catalog;
  onChange: (patch: Partial<EventDraft>) => void;
  locked: boolean;
}) {
  return (
    <div className="stack-sm">
      <div className="field-grid">
        <label className="field">
          <span>Key</span>
          <input
            value={draft.key}
            disabled={locked}
            onChange={(event) => onChange({ key: event.target.value })}
            placeholder="halloween-2026"
          />
          <small>Permanent — Pokemon minted by this event record it forever.</small>
        </label>

        <label className="field">
          <span>Name</span>
          <input value={draft.name} onChange={(event) => onChange({ name: event.target.value })} />
          <small>Shown on the card and the event page.</small>
        </label>
      </div>

      <label className="field">
        <span>Summary</span>
        <input
          value={draft.summary}
          maxLength={200}
          onChange={(event) => onChange({ summary: event.target.value })}
          placeholder="One line, shown on the card."
        />
      </label>

      <label className="field">
        <span>Description</span>
        <textarea
          rows={4}
          value={draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
          placeholder="What is happening, and why a player should take part."
        />
      </label>

      <div className="field-grid">
        <label className="field">
          <span>Starts</span>
          <input
            type="datetime-local"
            value={draft.startsAt}
            onChange={(event) => onChange({ startsAt: event.target.value })}
          />
        </label>
        <label className="field">
          <span>Ends</span>
          <input
            type="datetime-local"
            value={draft.endsAt}
            onChange={(event) => onChange({ endsAt: event.target.value })}
          />
        </label>
      </div>

      {/* PART 45. The times above are wall-clock in the admin's own browser;
          this states which clock that is, and it is what the dashboard echoes
          back later. An event opening seven hours early is almost always this
          field being assumed rather than read. */}
      <div className="pxwell admin-event-editor__tz">
        Times are in <strong>{draft.timezone}</strong>, your browser's zone.
        <br />
        Stored as UTC: {draft.startsAt ? new Date(draft.startsAt).toUTCString() : '—'} →{' '}
        {draft.endsAt ? new Date(draft.endsAt).toUTCString() : '—'}
      </div>

      <div className="field-grid field-grid--tight">
        <label className="field">
          <span>Type</span>
          <select value={draft.eventType} onChange={(event) => onChange({ eventType: event.target.value })}>
            {catalog.eventTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Theme</span>
          <select value={draft.theme} onChange={(event) => onChange({ theme: event.target.value })}>
            {THEMES.map((theme) => (
              <option key={theme} value={theme}>
                {theme}
              </option>
            ))}
          </select>
          <small>Sets the accent colour on cards and the event page.</small>
        </label>
        <label className="field">
          <span>Points called</span>
          <input
            value={draft.pointsName ?? ''}
            onChange={(event) => onChange({ pointsName: event.target.value || null })}
            placeholder="Festival Points"
          />
          <small>Leave blank for an event with no points.</small>
        </label>
      </div>

      <div className="field-row">
        <label className="field field--check">
          <input
            type="checkbox"
            checked={draft.visible}
            onChange={(event) => onChange({ visible: event.target.checked })}
          />
          <span>Visible in the events list</span>
        </label>
        <label className="field field--check">
          <input
            type="checkbox"
            checked={draft.featured}
            onChange={(event) => onChange({ featured: event.target.checked })}
          />
          <span>Featured on Home</span>
        </label>
      </div>
      <small className="muted">
        A hidden event still runs and still scores — that is what makes a staff test run the real page
        rather than an approximation of it.
      </small>
    </div>
  );
}

function ObjectivesTab({
  draft,
  catalog,
  onChange,
}: {
  draft: EventDraft;
  catalog: Catalog;
  onChange: (patch: Partial<EventDraft>) => void;
}) {
  const set = (index: number, patch: Partial<ObjectiveDraft>) => {
    const next = [...draft.objectives];
    next[index] = { ...next[index]!, ...patch };
    onChange({ objectives: next });
  };

  return (
    <div className="stack-sm">
      {draft.objectives.length === 0 && (
        <div className="pxwell admin-empty">
          No objectives yet — without one, nothing a player does earns points.
        </div>
      )}

      {draft.objectives.map((objective, index) => (
        <div key={index} className="pxwell admin-item">
          <div className="admin-item__head">
            <span className="admin-item__title">{objective.name || `Objective ${index + 1}`}</span>
            <button
              type="button"
              className="admin-item__remove"
              onClick={() => onChange({ objectives: draft.objectives.filter((_, i) => i !== index) })}
            >
              Remove
            </button>
          </div>

          <div className="field-grid">
            <label className="field">
              <span>Name</span>
              <input
                value={objective.name}
                placeholder="Hatch 25 Water Pokemon"
                onChange={(event) => set(index, { name: event.target.value })}
              />
            </label>
            <label className="field">
              <span>Key</span>
              <input value={objective.key} onChange={(event) => set(index, { key: event.target.value })} />
              <small>Stable id — progress is stored against it.</small>
            </label>
          </div>
          <div className="field-grid field-grid--tight">
            <label className="field">
              <span>Counts</span>
              <select value={objective.kind} onChange={(event) => set(index, { kind: event.target.value })}>
                {catalog.actions.map((action) => (
                  <option key={action.key} value={action.key}>
                    {action.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Target</span>
              <input
                type="number"
                min={1}
                value={objective.target}
                onChange={(event) => set(index, { target: Number(event.target.value) })}
              />
            </label>
            <label className="field">
              <span>Points each</span>
              <input
                type="number"
                min={0}
                value={objective.pointsEach}
                onChange={(event) => set(index, { pointsEach: Number(event.target.value) })}
              />
            </label>
          </div>
          <label className="field">
            <span>Only counts for these types</span>
            <input
              value={((objective.filter.types as string[]) ?? []).join(', ')}
              placeholder="water, ghost — leave blank to count every Pokemon"
              onChange={(event) => {
                const types = event.target.value
                  .split(',')
                  .map((value) => value.trim().toLowerCase())
                  .filter(Boolean);
                set(index, { filter: { ...objective.filter, types: types.length ? types : undefined } });
              }}
            />
          </label>
          <div className="field-row">
            <label className="field field--check">
              <input
                type="checkbox"
                checked={objective.repeatable}
                onChange={(event) => set(index, { repeatable: event.target.checked })}
              />
              <span>Keeps paying past the target</span>
            </label>
            <label className="field field--check">
              <input
                type="checkbox"
                checked={Boolean(objective.filter.shinyOnly)}
                onChange={(event) =>
                  set(index, {
                    filter: { ...objective.filter, shinyOnly: event.target.checked || undefined },
                  })
                }
              />
              <span>Shinies only</span>
            </label>
          </div>
        </div>
      ))}

      <Button
        variant="ghost"
        className="admin-add"
        onClick={() =>
          onChange({
            objectives: [
              ...draft.objectives,
              {
                key: `objective-${draft.objectives.length + 1}`,
                name: '',
                kind: catalog.actions[0]?.key ?? 'HATCH',
                filter: {},
                target: 10,
                pointsEach: 10,
                currencyEach: 0,
                pointsOnComplete: 0,
                repeatable: true,
                dailyLimit: null,
                accountLimit: null,
                progressDisplay: 'COUNTER',
                enabled: true,
              },
            ],
          })
        }
      >
        Add objective
      </Button>
    </div>
  );
}

function RewardsTab({
  draft,
  catalog,
  onChange,
  can,
  eventKey,
}: {
  draft: EventDraft;
  catalog: Catalog;
  onChange: (patch: Partial<EventDraft>) => void;
  can: (permission: string) => boolean;
  eventKey: string;
}) {
  const set = (index: number, patch: Partial<RewardDraft>) => {
    const next = [...draft.rewards];
    next[index] = { ...next[index]!, ...patch };
    onChange({ rewards: next });
  };

  return (
    <div className="stack-sm">
      {draft.rewards.length === 0 && (
        <div className="pxwell admin-empty">
          No rewards defined yet. Add one here, then attach it to a tier.
        </div>
      )}

      {draft.rewards.map((reward, index) => (
        <div key={index} className="pxwell admin-item">
          <div className="admin-item__head">
            <span className="admin-item__title">
              {reward.label || `Reward ${index + 1}`}
              {/* The kind beside the label, because "Anniversary Celebi" and
                  "10,000 Coins" are the same shape of row and very much not the
                  same kind of decision. */}
              <span className={`chip event-reward event-reward--${reward.kind.toLowerCase()}`}>
                {reward.kind}
              </span>
            </span>
            <button
              type="button"
              className="admin-item__remove"
              onClick={() => onChange({ rewards: draft.rewards.filter((_, i) => i !== index) })}
            >
              Remove
            </button>
          </div>

          <div className="field-grid field-grid--tight">
            <label className="field">
              <span>Label</span>
              <input
                value={reward.label}
                placeholder="5,000 PokeCoins"
                onChange={(event) => set(index, { label: event.target.value })}
              />
            </label>
            <label className="field">
              <span>Key</span>
              <input value={reward.key} onChange={(event) => set(index, { key: event.target.value })} />
            </label>
            <label className="field">
              <span>Type</span>
              <select
                value={reward.kind}
                onChange={(event) => set(index, { kind: event.target.value, config: {} })}
              >
                {catalog.rewardKinds
                  .filter((kind) => kind.key !== 'POKEMON' || can('events.pokemon'))
                  .map((kind) => (
                    <option key={kind.key} value={kind.key}>
                      {kind.label}
                    </option>
                  ))}
              </select>
            </label>
          </div>

          <RewardConfig
            reward={reward}
            catalog={catalog}
            eventKey={eventKey}
            onChange={(config) => set(index, { config })}
          />
        </div>
      ))}

      <Button
        variant="ghost"
        className="admin-add"
        onClick={() =>
          onChange({
            rewards: [
              ...draft.rewards,
              {
                key: `reward-${draft.rewards.length + 1}`,
                label: '',
                kind: 'COINS',
                config: { amount: 1000 },
                quantity: 1,
                enabled: true,
              },
            ],
          })
        }
      >
        Add reward
      </Button>
    </div>
  );
}

/**
 * The per-kind reward form.
 *
 * PART 20 is explicit that an admin must not have to type JSON, so every kind
 * gets real inputs backed by the server's own catalog — which also means a
 * reward cannot be configured against an item or badge that does not exist.
 */
function RewardConfig({
  reward,
  catalog,
  eventKey,
  onChange,
}: {
  reward: RewardDraft;
  catalog: Catalog;
  eventKey: string;
  onChange: (config: Record<string, unknown>) => void;
}) {
  const config = reward.config;
  const picker = (
    label: string,
    field: string,
    options: Array<{ key: string; name: string }>,
  ) => (
    <label className="field">
      <span>{label}</span>
      <select value={String(config[field] ?? '')} onChange={(event) => onChange({ ...config, [field]: event.target.value })}>
        <option value="">— pick one —</option>
        {options.map((option) => (
          <option key={option.key} value={option.key}>
            {option.name}
          </option>
        ))}
      </select>
    </label>
  );

  switch (reward.kind) {
    case 'COINS':
    case 'TOKENS':
    case 'CURRENCY':
      return (
        <label className="field">
          <span>Amount</span>
          <input
            type="number"
            min={1}
            value={Number(config.amount ?? 0)}
            onChange={(event) => onChange({ ...config, amount: Number(event.target.value) })}
          />
        </label>
      );
    case 'ITEM':
    case 'DECORATION':
    case 'TROPHY':
      return picker('Item', 'itemKey', catalog.items.map((item) => ({ key: item.key, name: item.name })));
    case 'EGG':
      return picker('Egg pool', 'eggFamilyKey', catalog.eggFamilies);
    case 'TITLE':
      return picker('Title', 'titleKey', catalog.titles);
    case 'BADGE':
      return picker('Badge', 'badgeKey', catalog.badges);
    case 'RIBBON':
      return picker('Ribbon', 'ribbonKey', catalog.ribbons);
    case 'POKEMON':
      return <PokemonRewardConfig config={config} catalog={catalog} eventKey={eventKey} onChange={onChange} />;
    default:
      return (
        <label className="field">
          <span>Cosmetic key</span>
          <input
            value={String(config.cosmeticKey ?? config.frameKey ?? config.backgroundKey ?? '')}
            onChange={(event) => onChange({ ...config, cosmeticKey: event.target.value })}
          />
        </label>
      );
  }
}

/**
 * The special-Pokemon builder, and the preview PART 20 asks for.
 *
 * The preview is fetched from the server and rendered from *its* answer rather
 * than from anything computed here, because the whole value of a preview is
 * that it used the same code the grant will. It also names which fields are
 * still a dice roll, so one sample is never mistaken for the outcome.
 */
function PokemonRewardConfig({
  config,
  catalog,
  eventKey,
  onChange,
}: {
  config: Record<string, unknown>;
  catalog: Catalog;
  eventKey: string;
  onChange: (config: Record<string, unknown>) => void;
}) {
  const toast = useToast();
  const spec = (config.pokemon as Record<string, unknown>) ?? {};
  const [preview, setPreview] = useState<PokemonPreview | null>(null);

  const set = (patch: Record<string, unknown>) => onChange({ ...config, pokemon: { ...spec, ...patch } });

  const runPreview = async () => {
    try {
      const result = await api.post<{ preview: PokemonPreview }>(
        `/admin/events/${eventKey}/preview-pokemon`,
        spec,
      );
      setPreview(result.preview);
    } catch (error) {
      toast.push('error', error instanceof ApiError ? error.message : 'Could not preview.');
    }
  };

  return (
    <div className="stack-sm">
      <div className="field-row">
        <label className="field">
          <span>Species</span>
          <select
            value={String(spec.speciesId ?? '')}
            onChange={(event) => set({ speciesId: Number(event.target.value) })}
          >
            <option value="">— pick one —</option>
            {catalog.species.map((species) => (
              <option key={species.id} value={species.id}>
                #{species.id} {species.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Level</span>
          <input
            type="number"
            min={1}
            max={100}
            value={Number(spec.level ?? 5)}
            onChange={(event) => set({ level: Number(event.target.value) })}
          />
        </label>
        <label className="field field--check">
          <input type="checkbox" checked={Boolean(spec.isShiny)} onChange={(event) => set({ isShiny: event.target.checked })} />
          <span>Shiny</span>
        </label>
      </div>

      <label className="field">
        <span>Event ribbon</span>
        <select value={String(spec.ribbonKey ?? '')} onChange={(event) => set({ ribbonKey: event.target.value || undefined })}>
          <option value="">— none —</option>
          {catalog.ribbons.map((ribbon) => (
            <option key={ribbon.key} value={ribbon.key}>
              {ribbon.name}
            </option>
          ))}
        </select>
      </label>

      {/* PART 39. Defaults are permissive, so every box here is an opt-in
          restriction — which is why they read as things you are turning off. */}
      <div className="field-row">
        {(
          [
            ['accountBound', 'Account bound'],
            ['tradeable', 'Tradeable'],
            ['breedable', 'Breedable'],
            ['releasable', 'Releasable'],
            ['bazaarAllowed', 'Bazaar'],
            ['giftable', 'Giftable'],
          ] as const
        ).map(([field, label]) => (
          <label key={field} className="field field--check">
            <input
              type="checkbox"
              checked={field === 'accountBound' ? Boolean(spec[field]) : spec[field] !== false}
              onChange={(event) =>
                set({ [field]: field === 'accountBound' ? event.target.checked : event.target.checked })
              }
            />
            <span>{label}</span>
          </label>
        ))}
      </div>

      <Button variant="ghost" onClick={runPreview} disabled={!spec.speciesId}>
        Preview Pokemon
      </Button>

      {preview && (
        <div className="pxwell admin-pokemon-preview">
          {(preview.isShiny ? preview.species.spriteShiny : preview.species.spriteFront) && (
            <img
              className="pixel"
              src={(preview.isShiny ? preview.species.spriteShiny : preview.species.spriteFront)!}
              alt=""
            />
          )}
          <div className="admin-pokemon-preview__body">
            <div className="admin-pokemon-preview__name">
              {preview.isShiny && '✨ '}
              {preview.species.displayName}
              <span className="muted"> · Lv {preview.level}</span>
            </div>
            <div className="muted">
              {preview.gender} · {preview.nature?.name ?? 'random nature'}
            </div>
            <div className="muted">
              {preview.originLabel} · OT {preview.originalTrainer}
            </div>
            {/* Naming what is still a dice roll matters: without it one sample
                reads as the outcome, and an admin approves a Pokemon believing
                its IVs are fixed when every copy will differ. */}
            <div className="muted">
              Rolled per copy:{' '}
              {[
                preview.randomised.ivs.length > 0 ? 'IVs' : null,
                preview.randomised.gender ? 'gender' : null,
                preview.randomised.nature ? 'nature' : null,
              ]
                .filter(Boolean)
                .join(', ') || 'nothing — every copy identical'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TiersTab({
  draft,
  catalog,
  onChange,
}: {
  draft: EventDraft;
  catalog: Catalog;
  onChange: (patch: Partial<EventDraft>) => void;
}) {
  const set = (index: number, patch: Partial<TierDraft>) => {
    const next = [...draft.tiers];
    next[index] = { ...next[index]!, ...patch };
    onChange({ tiers: next });
  };

  return (
    <div className="stack-sm">
      {draft.tiers.length === 0 && (
        <div className="pxwell admin-empty">
          No reward tiers yet. A tier is a points milestone and what it hands over.
        </div>
      )}

      {draft.tiers.map((tier, index) => (
        <div key={index} className="pxwell admin-item">
          <div className="admin-item__head">
            <span className="admin-item__title">
              {formatNumber(tier.threshold)} points
              {tier.label ? ` · ${tier.label}` : ''}
            </span>
            <button
              type="button"
              className="admin-item__remove"
              onClick={() => onChange({ tiers: draft.tiers.filter((_, i) => i !== index) })}
            >
              Remove
            </button>
          </div>

          <div className="field-grid">
            <label className="field">
              <span>At points</span>
              <input
                type="number"
                min={0}
                value={tier.threshold}
                onChange={(event) => set(index, { threshold: Number(event.target.value) })}
              />
            </label>
            <label className="field">
              <span>Label</span>
              <input
                value={tier.label}
                placeholder="Festival banner"
                onChange={(event) => set(index, { label: event.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span>Grants</span>
            <select
              multiple
              value={tier.rewards}
              onChange={(event) =>
                set(index, {
                  rewards: Array.from(event.target.selectedOptions).map((option) => option.value),
                })
              }
            >
              {draft.rewards.map((reward) => (
                <option key={reward.key} value={reward.key}>
                  {reward.label || reward.key}
                </option>
              ))}
            </select>
            <small>
              {draft.rewards.length === 0
                ? 'Define a reward first, on the Rewards tab.'
                : 'Ctrl- or Cmd-click to grant more than one.'}
            </small>
          </label>

          <div className="field-grid">
            <label className="field">
              <span>Who qualifies</span>
              <select
                value={tier.eligibility}
                onChange={(event) => set(index, { eligibility: event.target.value })}
              >
                {catalog.eligibility.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field--check">
              <input
                type="checkbox"
                checked={tier.autoGrant}
                onChange={(event) => set(index, { autoGrant: event.target.checked })}
              />
              <span>Give automatically (no Claim button)</span>
            </label>
          </div>
        </div>
      ))}

      <Button
        variant="ghost"
        className="admin-add"
        onClick={() =>
          onChange({
            tiers: [
              ...draft.tiers,
              {
                threshold: (draft.tiers.at(-1)?.threshold ?? 0) + 250,
                label: '',
                autoGrant: false,
                eligibility: 'PARTICIPANT',
                eligibilityConfig: {},
                enabled: true,
                rewards: [],
              },
            ],
          })
        }
      >
        Add tier
      </Button>
    </div>
  );
}

function ModifiersTab({
  draft,
  catalog,
  onChange,
}: {
  draft: EventDraft;
  catalog: Catalog;
  onChange: (patch: Partial<EventDraft>) => void;
}) {
  const modifiers = draft.modifiers as Record<string, { mult?: number; stackGroup?: string }>;

  return (
    <div className="stack-sm">
      <div className="pxwell admin-event-editor__tz">
        Bonuses apply only while the event is <strong>ACTIVE</strong> and are never written into the
        game's own settings — when the event ends they simply stop. Within a stack group the best
        bonus wins rather than multiplying, and every one is capped.
      </div>

      <div className="field-grid field-grid--tight">
        {catalog.modifiers.map((modifier) => {
          const current = modifiers[modifier.key];
          const active = current?.mult != null && current.mult !== 1;
          // The Bazaar fee is the one place a value *below* 1 is the generous
          // direction — a market weekend halves the cut — so it alone may go
          // under 1. Every other key would only be a nerf below 1, and an event
          // that quietly makes the game worse is not what this screen is for.
          const lowerIsBetter = modifier.key === 'bazaar.fee';
          // A knob the game does not read is disabled rather than hidden: an
          // admin should be able to see that the setting exists and is not
          // connected, instead of setting it, publishing, and finding out from
          // players that nothing changed.
          const wired = modifier.implemented !== false;

          return (
            <label
              key={modifier.key}
              className={`field ${wired ? '' : 'field--unwired'}`}
              title={wired ? undefined : 'Not yet read by the game.'}
            >
              <span>{modifier.label}</span>
              <input
                type="number"
                step="0.1"
                min={lowerIsBetter ? 0 : 1}
                max={modifier.cap ?? 5}
                value={current?.mult ?? 1}
                disabled={!wired}
                onChange={(event) => {
                  const mult = Number(event.target.value);
                  const next = { ...modifiers };
                  if (mult === 1 || Number.isNaN(mult)) delete next[modifier.key];
                  else next[modifier.key] = { mult, stackGroup: modifier.key.split('.')[0]! };
                  onChange({ modifiers: next });
                }}
              />
              <small>
                {!wired
                  ? 'Not wired up yet'
                  : active
                    ? lowerIsBetter
                      ? `${Math.round((1 - (current!.mult ?? 1)) * 100)}% off the fee`
                      : `${current!.mult}× while this event runs`
                    : `Off · up to ${modifier.cap ?? 5}×`}
              </small>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ReviewTab({
  issues,
  eventKey,
  status,
  can,
}: {
  issues: { errors: ValidationIssue[]; warnings: ValidationIssue[] } | null;
  eventKey: string;
  status: Status;
  can: (permission: string) => boolean;
}) {
  const analytics = useAsync<Analytics | null>(
    () =>
      can('events.analytics') && status !== 'DRAFT'
        ? api.get(`/admin/events/${eventKey}/analytics`)
        : Promise.resolve(null as never),
    [eventKey, status],
  );

  return (
    <div className="stack-sm">
      {issues == null ? (
        <div className="pxwell admin-empty">
          Press Save to run the pre-publish checks.
        </div>
      ) : (
        <>
          {issues.errors.length === 0 && issues.warnings.length === 0 && (
            <div className="pxwell admin-event-ok">
              <strong>Everything checks out.</strong> Nothing here will fail in front of a player.
            </div>
          )}
          {/* Errors first and always: a warning is something to think about,
              an error is something that breaks at claim time. */}
          {issues.errors.map((issue, index) => (
            <div key={`e${index}`} className="pxwell admin-event-issue admin-event-issue--error">
              <strong>{issue.where}</strong>
              {issue.message}
            </div>
          ))}
          {issues.warnings.map((issue, index) => (
            <div key={`w${index}`} className="pxwell admin-event-issue admin-event-issue--warn">
              <strong>{issue.where}</strong>
              {issue.message}
            </div>
          ))}
        </>
      )}

      {analytics.data && (
        <Panel title="Participation" collapsibleKey="admin-event-analytics">
          <div className="stat-row">
            <Stat label="Taking part" value={analytics.data.participation.participants} />
            <Stat label="Active 24h" value={analytics.data.participation.activeLast24h} />
            <Stat label="Points earned" value={analytics.data.participation.pointsTotal} />
            <Stat label="Rewards claimed" value={analytics.data.rewards.claimsTotal} />
          </div>
          {/* Only shown when the event has actually minted Pokemon. Rarity is
              the thing an operator has to maintain, and "still with the trainer
              it was given to" is the number that says whether it is holding. */}
          {analytics.data.pokemon.generated > 0 && (
            <div className="stat-row">
              <Stat label="Pokemon minted" value={analytics.data.pokemon.generated} />
              <Stat label="Still with OT" value={analytics.data.pokemon.withOriginalTrainer} />
              <Stat label="Traded" value={analytics.data.pokemon.traded} />
              <Stat label="Released" value={analytics.data.pokemon.released} />
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat">
      <div className="stat__value">{formatNumber(value ?? 0)}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}
